import React, {useEffect, useCallback, useMemo, useState} from 'react';
import {useAppDispatch, useAppSelector} from "../../../app/store";
import {Alert, Button, Col, Form, Modal, Row, Tab, Tabs} from "react-bootstrap";
import {
    changeSectionModal,
    closeSectionModal,
    setActiveLanguage,
    getSection,
    saveNewSection,
    saveSection, deleteSection
} from "../../../app/slices/modals/sectionEditModal";
import {ADMIN_LANG} from "../../../settings";
import AdminLoader from "../AdminLoader";
import {PlainDetails} from "../../../types/admin";
import AsyncThemeSelect from "../gameView/AsyncThemeSelect";
import Confirm from "../Confirm";
import {openModal as openThemeModal} from "../../../app/slices/modals/themeModal";
import {AsyncOptions} from "./AsyncOptions";
import {v4 as uuidv4} from "uuid";
import ImageField from "../ImageField";

const SectionEditModal = ({is_draft}: { is_draft: boolean }) => {
    const dispatch = useAppDispatch();
    const {game_id, current_version_id, active_languages} = useAppSelector((state) => state.adminGameView);
    const {
        isOpen,
        section_id,
        languages,
        loading,
        languageTab,
        error,
        theme,
        theme_options,
        background_image,
    } = useAppSelector((state) => state.sectionEditModal);

    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [backgroundImage, setBackgroundImage] = useState<File | null>(null);




    useEffect(() => {
        if (isOpen && section_id && game_id && current_version_id) {
            dispatch(getSection({game_id, section_id, version_id: current_version_id}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, section_id, game_id, current_version_id]);

    const handleClose = useCallback(() => {
        dispatch(closeSectionModal());
    }, [dispatch]);

    const setImage = (image: File | null) => {
        if (image) {
            setBackgroundImage(image);
        } else {
            setBackgroundImage(null);
            dispatch(changeSectionModal({background_image: null}));
        }
    }

    const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const form = e.currentTarget;

        if (!form.checkValidity()) {
            e.stopPropagation();
        } else {
            const formData = new FormData();
            formData.append('game_id', game_id);
            formData.append('version_id', current_version_id);
            formData.append('languages', JSON.stringify(languages));
            formData.append('theme', JSON.stringify(theme));
            if (backgroundImage) {
                formData.append('background_image', backgroundImage, uuidv4() + backgroundImage.name);
            } else if (background_image) {
                formData.append('background_image', background_image.name);
            }

            if(section_id){
               formData.append('section_id', section_id);
               dispatch(saveSection(formData));
            } else {
                dispatch(saveNewSection(formData));
            }
        }
    }

    const handleDelete = () => {
        if (section_id) {
            setShowConfirmDelete(true);
        }
    };

    const handleConfirm = () => {
        setShowConfirmDelete(false);
        if (section_id) {
            dispatch(deleteSection({section_id, game_id, version_id: current_version_id}));
        }
    };

    const handleTabSelect = useCallback((key: string | null) => {
        if (key) {
            dispatch(setActiveLanguage(key));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleThemeChange = useCallback((theme: PlainDetails) => {
        dispatch(changeSectionModal({theme: theme}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleNameChange = useCallback((name: string) => {
        const updatedLanguages = languages.map((language) =>
            language.language_code === languageTab
                ? {...language, name}
                : language
        );
        if (!updatedLanguages.some(lang => lang.language_code === languageTab)) {
            updatedLanguages.push({language_code: languageTab, name});
        }
        dispatch(changeSectionModal({languages: updatedLanguages}));
    }, [languages, languageTab, dispatch]);

    const sectionName = useMemo(() => {
        if (section_id) {
            const section = languages.find(lang => lang.language_code === languageTab);
            return section?.name || '';
        }
        return ADMIN_LANG.new_section;
    }, [section_id, languages, languageTab]);

    const nameHelpText = languages
        .filter(lang => lang.language_code !== languageTab && lang.name)
        .map(lang => (
            <div key={lang.language_code}>
                <strong>{lang.language_code.toUpperCase()}: </strong>
                {lang.name}
            </div>
        ));


    const handleAddNewTheme = () => {
        if (section_id) {
            dispatch(openThemeModal({theme_id: null, section_id}));
        } else {
            dispatch(openThemeModal({theme_id: null, section_id: 'new_section'}));
        }
    }

    const handleViewTheme = (item: PlainDetails) => {
        if (section_id && item) {
            dispatch(openThemeModal({theme_id: item.value, section_id}));
        }else{
            dispatch(openThemeModal({theme_id: item.value, section_id: 'new_section'}));
        }
    }

    const handleRemoveTheme = () => {
        dispatch(changeSectionModal({theme: null}));
    }

    const languageWarning = languages.find(
        lang => lang.language_code === languageTab && lang.warnings
    ) || {warnings: ''};


    return (
        <Modal size='xl' show={isOpen} onHide={handleClose} className='admin-modal'>
            <Modal.Header closeButton>
                <Modal.Title>{sectionName}</Modal.Title>
            </Modal.Header>
            {loading && <AdminLoader/>}
            <Modal.Body>
                {error && (
                    <Alert variant='danger' dismissible>
                        <div dangerouslySetInnerHTML={{__html: error}}/>
                    </Alert>
                )}
                {languageWarning.warnings && (
                    <Alert variant='warning'>
                        <div dangerouslySetInnerHTML={{__html: languageWarning.warnings}}/>
                    </Alert>
                )}
                <Form id='section-modal-form' onSubmit={handleSave}>
                    <Tabs activeKey={languageTab} onSelect={handleTabSelect}>
                        {active_languages.map(language => (
                            <Tab eventKey={language} title={language.toUpperCase()} key={language}>
                                <Row className='gy-3 pt-3'>
                                    <Col xs={12}>
                                        <Form.Group controlId={`name_${language}`}>
                                            <Form.Label>{`${ADMIN_LANG.name} (${language.toUpperCase()})`}</Form.Label>
                                            <Form.Control
                                                type='text'
                                                maxLength={255}
                                                onChange={(e) => handleNameChange(e.target.value)}
                                                value={languages.find((lang) => lang.language_code === language)?.name || ''}
                                                required={language === languageTab}
                                            />
                                            {nameHelpText.length > 0 &&
                                                <Form.Text className='help-text'>
                                                    {nameHelpText}
                                                </Form.Text>
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Group controlId={`theme_${languageTab}`}>
                                            <Form.Label>{ADMIN_LANG.theme}</Form.Label>
                                            <div className='d-flex align-items-center'>
                                                <div className="flex-grow-1">
                                                    <AsyncThemeSelect onChange={handleThemeChange} initialValue={theme}
                                                                      defaultOptions={theme_options}/>
                                                </div>
                                                <AsyncOptions item={theme} handleAddNew={handleAddNewTheme}
                                                              handleViewItem={handleViewTheme}
                                                              handleRemoveSelection={handleRemoveTheme}/>
                                            </div>
                                            <Form.Text className='help-text'
                                                       dangerouslySetInnerHTML={{__html: ADMIN_LANG.theme_help_section}}></Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Group className='d-flex flex-column' controlId={`image_${languageTab}`}>
                                            <ImageField defaultImage={background_image}
                                                        setImage={setImage}
                                                        label={ADMIN_LANG.background_image}
                                                        help_text={ADMIN_LANG.section_background_help}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Tab>
                        ))}
                    </Tabs>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {section_id && is_draft && (
                    <Button variant='danger' className='me-auto' onClick={handleDelete}>
                        {ADMIN_LANG.delete}
                    </Button>
                )}
                <Button variant='outline-primary' onClick={handleClose}>
                    {ADMIN_LANG.cancel}
                </Button>
                <Button
                    variant='primary'
                    form='section-modal-form'
                    type='submit'
                >
                    {ADMIN_LANG.save}
                </Button>

            </Modal.Footer>
            <Confirm
                show={showConfirmDelete}
                message={ADMIN_LANG.confirm_delete_section}
                onConfirm={handleConfirm}
                onCancel={() => setShowConfirmDelete(false)}
            />
        </Modal>
    )
}

export default SectionEditModal;